import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../../context/OrganizationContext';
import { getOrgIcon, getOrgColorClass } from './orgStyles';

/** Slack/Discord uslubidagi vertikal tashkilotlar ustuni kengligi (px). */
export const ORG_RAIL_WIDTH = 68;

/**
 * Sidebar'ning chap chetidagi tashkilotlar ustuni.
 * - Har bir tashkilot rangli ikonka, bir bosishda almashadi.
 * - Faol tashkilot chap tomonda brand rangli "pill" va halqa bilan belgilanadi.
 * - Hover qilinganda to'liq nomi tooltip'da chiqadi (portal orqali, sidebar overflow'idan qirqilmaydi).
 * - Kamida 1 ta tashkilot bo'lsa ko'rsatiladi — `useShowOrgRail()` bilan tekshiring.
 */
export default function OrganizationRail({ className = '' }) {
    const { organizations, currentOrg, switchOrg, getLocalizedName } = useOrganization();
    const { t, i18n } = useTranslation();
    const [tooltip, setTooltip] = useState(null); // { name, top, left }
    const [hoveredId, setHoveredId] = useState(null);
    const lang = i18n.language || 'uz';

    function showTooltip(e, org) {
        const r = e.currentTarget.getBoundingClientRect();
        setTooltip({ name: getLocalizedName(org, lang), top: r.top + r.height / 2, left: r.right + 10 });
        setHoveredId(org.id);
    }
    function hideTooltip() {
        setTooltip(null);
        setHoveredId(null);
    }

    return (
        <nav
            aria-label={t('organizations.label', 'Tashkilot')}
            style={{ width: ORG_RAIL_WIDTH }}
            className={`h-full flex-shrink-0 flex flex-col items-center gap-3 pt-4 pb-3 bg-surface-2/70 border-r border-border overflow-y-auto overflow-x-hidden ${className}`}
        >
            {organizations.map((org) => {
                const isActive = org.id === currentOrg?.id;
                const isHovered = hoveredId === org.id;
                const Icon = getOrgIcon(org);
                const name = getLocalizedName(org, lang);
                const shortName = org.code ? org.code.replace(/_/g, ' ').toUpperCase() : name;

                return (
                    <div key={org.id} className="relative w-full flex flex-col items-center">
                        {/* Faol / hover indikatori (chap chetdagi pill) */}
                        <motion.span
                            initial={false}
                            animate={{ height: isActive ? 32 : isHovered ? 16 : 0, opacity: isActive || isHovered ? 1 : 0 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                            className="absolute left-0 top-[20px] -translate-y-1/2 w-1 rounded-r-full bg-brand"
                        />
                        <button
                            type="button"
                            onClick={() => !isActive && switchOrg(org.id)}
                            onMouseEnter={(e) => showTooltip(e, org)}
                            onMouseLeave={hideTooltip}
                            onFocus={(e) => showTooltip(e, org)}
                            onBlur={hideTooltip}
                            aria-label={name}
                            aria-current={isActive ? 'true' : undefined}
                            id={`org-rail-${org.code || org.id}`}
                            className={`w-10 h-10 flex items-center justify-center border transition-all duration-200 overflow-hidden
                                ${getOrgColorClass(org)}
                                ${isActive
                                    ? 'rounded-xl ring-2 ring-brand ring-offset-2 ring-offset-surface scale-100'
                                    : 'rounded-2xl hover:rounded-xl opacity-75 hover:opacity-100 hover:scale-105'}
                            `}
                        >
                            {org.logo_url
                                ? <img src={org.logo_url} alt="" className="w-6 h-6 object-contain" />
                                : <Icon className="w-5 h-5" />}
                        </button>
                        <span
                            className={`mt-1.5 w-full px-1.5 text-center whitespace-normal break-words line-clamp-2 text-[9px] leading-[1.2] font-semibold tracking-wide transition-colors
                                ${isActive ? 'text-brand' : 'text-muted'}`}
                        >
                            {shortName}
                        </span>
                    </div>
                );
            })}

            {/* Tooltip — body'ga portal qilinadi */}
            {createPortal(
                <AnimatePresence>
                    {tooltip && (
                        <motion.div
                            key={tooltip.name}
                            initial={{ opacity: 0, x: -4, y: '-50%' }}
                            animate={{ opacity: 1, x: 0, y: '-50%' }}
                            exit={{ opacity: 0, x: -4, y: '-50%' }}
                            transition={{ duration: 0.12 }}
                            style={{ top: tooltip.top, left: tooltip.left }}
                            className="fixed z-[100] pointer-events-none flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface border border-border text-primary text-xs font-semibold shadow-xl whitespace-nowrap"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-brand flex-shrink-0" />
                            {tooltip.name}
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </nav>
    );
}

/** Rail ko'rsatilishi kerakmi (kamida 1 ta tashkilot bo'lsa — joriy tashkilot doim ko'rinib tursin). */
export function useShowOrgRail() {
    const { organizations, loading } = useOrganization();
    return !loading && organizations.length > 0;
}

/** Sidebar header'ida "BI by CAU" ostida joriy tashkilot nomi. */
export function CurrentOrgCaption({ className = '' }) {
    const { currentOrg, getLocalizedName } = useOrganization();
    const { i18n } = useTranslation();
    if (!currentOrg) return null;
    return (
        <p className={`text-[11px] text-muted truncate leading-tight ${className}`}>
            {getLocalizedName(currentOrg, i18n.language || 'uz')}
        </p>
    );
}
